import { Component, inject, OnInit, signal } from '@angular/core';
import {CookieConsentPreferences, CookieConsentService} from '../../services/cookie-consent.service';
import {COOKIE_CONSENT_COOKIE_NAME} from '../../services/cookie-constants';

export interface CookieType {
  name: string;
  purpose: string;
  duration: string;
}

export interface CookieCategory {
  id: string;
  label: string;
  essential: boolean;
  description?: string;  // <-- needs to be here
  expanded: ReturnType<typeof signal<boolean>>;
  cookies: CookieType[];
}

export interface CookiePreferences {
  [categoryId: string]: boolean;
}

@Component({
  selector: 'app-cookie-content-component',
  standalone: true,
  imports: [],
  templateUrl: './cookie-content-component.html',
  styleUrl: './cookie-content-component.sass',
})
export class CookieContentComponent implements OnInit {
  private consentService = inject(CookieConsentService);

  draft = signal<CookieConsentPreferences>({
    advertising: false,
    analytics: false,
  });

  readonly categories: CookieCategory[] = [
    {
      id: 'essential',
      label: 'Essential Cookies',
      essential: true,
      expanded: signal(false),
      description: 'These cookies are essential for the site and account features to function. ' +
        'Some are erased when you close the browser. Others stay longer, sometimes indefinitely, ' +
        'and are saved onto your device so that they\'re there when you come back.',
      cookies: [
        {
          name: COOKIE_CONSENT_COOKIE_NAME,
          purpose: 'Stores whether you have accepted cookies on this site.',
          duration: '12 months',
        },
        {
          name: 'cgp_auth',
          purpose: 'Keeps you signed in across browser sessions by persisting your login data securely.',
          duration: '30 days',
        },
        {
          name: 'CgpDigitalRevision',
          purpose: 'Remembers your selected revision tier (Higher or Foundation) so your preference is applied on your next visit.',
          duration: '1 day',
        },
        {
          name: 'Switcher cookie (site-defined name)',
          purpose: 'Set when a product switcher is available, enabling navigation between related CGP digital products. Shared across cgpbooks.co.uk subdomains.',
          duration: '1 day',
        },
        {
          name: 'Session cookies',
          purpose: 'Keep you signed in and support secure navigation during your visit.',
          duration: 'Session only',
        },
      ],
    },
    // {
    //   id: 'advertising',
    //   label: 'Advertising',
    //   essential: false,
    //   expanded: signal(false),
    //   cookies: [],  // populate when you have advertising cookie details
    // },
    {
      id: 'analytics',
      label: 'Analytics',
      essential: false,
      description:
        'CGP use analytical cookies, such as those provided by Google Analytics, ' +
        'so we can improve your overall browsing experience. These cookies help us understand ' +
        'how visitors interact with our website by collecting data about your browsing activity.',
      expanded: signal(false),
      cookies: [
        {
          name: '_ga',
          purpose: 'Used by Google Analytics to identify individual users by assigning a unique ID, helping website owners understand how visitors interact with their site over time.',
          duration: '2 years (browsers may cap at 400 days)',
        },
        {
          name: '_ga_*',
          purpose: 'Works alongside the _ga cookie and Google Analytics 4 to manage session data and track user activity more accurately during each visit.',
          duration: '2 years (browsers may cap at 400 days)',
        },
      ],
    },
  ];

  get optionalCategories(): CookieCategory[] {
    return this.categories.filter(c => !c.essential);
  }

  get essentialCategory(): CookieCategory {
    return this.categories.find(c => c.essential)!;
  }

  ngOnInit(): void {
    this.draft.set({ ...this.consentService.preferences() });
  }

  isEnabled(categoryId: string): boolean {
    const key = categoryId as keyof CookieConsentPreferences;
    return this.draft()[key] ?? false;
  }

  setConsent(categoryId: string, value: boolean): void {
    const key = categoryId as keyof CookieConsentPreferences;
    this.draft.update(d => ({ ...d, [key]: value }));
  }

  toggleExpanded(category: CookieCategory): void {
    category.expanded.update(v => !v);
  }

  savePreferences(): void {
    this.consentService.savePreferences({ ...this.draft() });
  }

  rejectAll(): void {
    const rejected: CookieConsentPreferences = {
      advertising: false,
      analytics: false,
    };
    this.draft.set(rejected);
    this.consentService.savePreferences(rejected);
  }

  acceptAll(): void {
    const accepted: CookieConsentPreferences = {
      advertising: true,
      analytics: true,
    };
    this.draft.set(accepted);
    this.consentService.savePreferences(accepted);
  }
}
